import { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { getUnrestrictedLearningProgramSlidePreviewService } from '../../../../../services/learning_programs.service';
import {
    buildDeckHtml, collectDeckCss, flattenSessionSlides, listProgramSessions,
} from '../../utils/LearnTab/slideDeck.utils';
import { toastWarning } from '../../../../../utils/utils';
import { tUi } from '../../../../../utils/dq_lc';

const ASSET_WAIT_MS = 10000;

// Resolves once every image under `root` has loaded or failed and fonts are ready (capped by a timeout), so
// layouts that measure themselves have settled before the markup is captured.
const waitForAssets = (root) => {
    const images = Array.from(root?.querySelectorAll('img') || []);
    const loads = images.map(img => (img.complete ? Promise.resolve() : new Promise(resolve => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
    })));
    const fonts = (typeof document !== 'undefined' && document.fonts?.ready) || Promise.resolve();
    return Promise.race([Promise.all([...loads, fonts]), new Promise(resolve => setTimeout(resolve, ASSET_WAIT_MS))]);
};

// Fetches every session's slides of a public-preview program, renders them off-screen and saves them as one
// standalone HTML file (images and videos stay linked to their URLs). `deck` is non-null only while exporting.
const useSlideDeckHtml = ({ courseCode, modules, programTitle, uiLocale = 'en-US' }) => {
    const deckRef = useRef(null);
    const [deck, setDeck] = useState(null);
    const [exporting, setExporting] = useState(false);

    const finish = useCallback(() => {
        setDeck(null);
        setExporting(false);
    }, []);

    const startExport = useCallback(async () => {
        if (exporting || !courseCode) return;
        setExporting(true);
        const sessions = [];
        // Sequential on purpose: every response carries all languages and the deck can be large.
        for (const session of listProgramSessions(modules)) {
            let result = null;
            try {
                result = await getUnrestrictedLearningProgramSlidePreviewService(courseCode, session.session_code);
            } catch (e) {
                result = null;
            }
            sessions.push({ ...session, ...flattenSessionSlides(result?.data) });
        }
        if (!sessions.some(session => session.slides.length)) {
            toastWarning(tUi('preview.deck.failed', uiLocale));
            finish();
            return;
        }
        setDeck(sessions);
    }, [exporting, courseCode, modules, uiLocale, finish]);

    useEffect(() => {
        if (!deck) return undefined;
        let cancelled = false;
        waitForAssets(deckRef.current).then(() => {
            if (cancelled || !deckRef.current) return;
            try {
                const clone = deckRef.current.cloneNode(true);
                // Absolute media URLs, so the saved file does not depend on this site's paths.
                clone.querySelectorAll('img[src], video[src], source[src]').forEach((el) => el.setAttribute('src', el.src));
                const html = buildDeckHtml({
                    title: `${programTitle || courseCode} - Slides`,
                    css: collectDeckCss(document),
                    bodyHtml: clone.outerHTML,
                });
                saveAs(new Blob([html], { type: 'text/html;charset=utf-8' }), `${courseCode}_slides.html`);
            } catch (e) {
                toastWarning(tUi('preview.deck.failed', uiLocale));
            }
            finish();
        });
        return () => { cancelled = true; };
    }, [deck, courseCode, programTitle, uiLocale, finish]);

    return { deckRef, deck, exporting, startExport };
};

export default useSlideDeckHtml;
